import { useMemo, useState, useCallback, useEffect, useRef, useLayoutEffect } from 'react';
import {
  ScatterChart, Scatter,
  XAxis, YAxis, CartesianGrid,
  ReferenceLine, Customized, ResponsiveContainer,
  useXAxisScale, useYAxisScale,
} from 'recharts';
import { LAP_COLORS, getLapColor } from '../LapSelector/LapSelector';
import PanelHeader from '../WindowFrame/headerSlot';
import HelpButton from '../HelpButton/HelpButton';
import styles from './FrictionCircle.module.css';
import { useTheme } from '../../contexts/ThemeContext';
import { lastCursor } from '../../utils/chartCursor';
import { exportChartPng } from '../../utils/chartExport';

const M_TO_FT = 3.28084;

function hexToRgba(hex, alpha) {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function HitDot({ cx, cy, r }) {
  return <circle cx={cx} cy={cy} r={r || 4} fill="transparent" />;
}

function drawCanvas(canvas, xScale, yScale, series, dotSize, hoverDistFt) {
  if (!canvas || !xScale || !yScale) return;

  const dpr  = window.devicePixelRatio || 1;
  const cssW = canvas.offsetWidth;
  const cssH = canvas.offsetHeight;
  if (!cssW || !cssH) return;

  canvas.width  = Math.round(cssW * dpr);
  canvas.height = Math.round(cssH * dpr);

  const ctx = canvas.getContext('2d');
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, cssW, cssH);

  for (const s of series) {
    ctx.fillStyle = hexToRgba(s.color, 0.75);
    for (const pt of s.points) {
      const cx = xScale(pt.x);
      const cy = yScale(pt.y);
      if (!isFinite(cx) || !isFinite(cy)) continue;
      ctx.beginPath();
      ctx.arc(cx, cy, dotSize, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  // Draw a highlighted dot at the nearest data point to the hovered distance
  if (hoverDistFt != null) {
    for (const s of series) {
      let nearest = null, minDelta = Infinity;
      for (const pt of s.points) {
        if (pt.dist_ft == null) continue;
        const d = Math.abs(pt.dist_ft - hoverDistFt);
        if (d < minDelta) { minDelta = d; nearest = pt; }
      }
      if (!nearest) continue;
      const cx = xScale(nearest.x);
      const cy = yScale(nearest.y);
      if (!isFinite(cx) || !isFinite(cy)) continue;
      const r = dotSize + 3;
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fillStyle = s.color;
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }
  }
}

// Captures Recharts scale functions and triggers a canvas redraw whenever
// Recharts re-renders (data change, resize). Hover redraws bypass this
// component entirely via rAF — no React re-render needed.
function CanvasDotsLayer({ canvasRef, scalesRef, onDraw }) {
  const xScale = useXAxisScale();
  const yScale = useYAxisScale();

  useLayoutEffect(() => {
    if (xScale) scalesRef.current.xScale = xScale;
    if (yScale) scalesRef.current.yScale = yScale;
    onDraw();
  });

  return null;
}

function downsample(arr, maxPts) {
  if (arr.length <= maxPts) return arr;
  const step = arr.length / maxPts;
  return Array.from({ length: maxPts }, (_, i) => arr[Math.round(i * step)]);
}

// Draws a grip-limit ellipse in chart pixel space using axis scale functions.
// Using <ellipse> here because the chart may not be square even when the data
// domain is symmetric — rx/ry independently map the G value to pixels.
function GripCircle({ gripLimit }) {
  const xScale = useXAxisScale();
  const yScale = useYAxisScale();
  if (!xScale || !yScale) return null;

  const cx = xScale(0);
  const cy = yScale(0);
  const rx = Math.abs(xScale(gripLimit) - xScale(0));
  const ry = Math.abs(yScale(gripLimit) - yScale(0));

  return (
    <ellipse
      cx={cx} cy={cy}
      rx={rx} ry={ry}
      fill="none"
      stroke="#2a4a2a"
      strokeWidth={1.5}
      strokeDasharray="5 3"
    />
  );
}

function buildSeries(lapTraces, selectedLaps, colors, colorOffset = 0, keyPrefix = '', zoomFt = null, customColors = null) {
  return selectedLaps.map((lapIndex, i) => {
    const trace = lapTraces[lapIndex];
    if (!trace) return null;

    // Require both G values AND GPS coordinates so map crosshair can fire
    let raw = trace.points.filter(
      p => p.lat_g != null && p.lon_g != null && p.lat != null && p.lon != null
    );
    if (!raw.length) return null;

    // When a distance-based zoom is active, only show points in that window
    if (zoomFt) {
      raw = raw.filter(p => {
        const ft = (p.dist_m ?? 0) * M_TO_FT;
        return ft >= zoomFt[0] && ft <= zoomFt[1];
      });
      if (!raw.length) return null;
    }

    const sampled = downsample(raw, 5000);
    const lapStartTime = trace.start_time ?? 0;
    const points  = sampled.map(p => ({
      x:        Math.round(p.lat_g * 100) / 100,
      y:        Math.round(p.lon_g * 100) / 100,
      lat:      p.lat,
      lon:      p.lon,
      dist_ft:  (p.dist_m ?? 0) * M_TO_FT,
      csv_time: lapStartTime + (p.time ?? 0),
    }));

    return {
      key:    `${keyPrefix}${lapIndex}`,
      color:  (customColors && customColors[lapIndex]) || colors[(colorOffset + i) % colors.length],
      label:  trace.lap_time_str || `L${trace.lap_number}`,
      points,
    };
  }).filter(Boolean);
}

const fmt2 = (v) => (v != null ? v.toFixed(2) : '—');

function dispatchHover(lat, lon, distFt) {
  window.dispatchEvent(new CustomEvent('trackapp:chart-hover', {
    detail: (lat != null && lon != null) ? { lat, lon, distFt: distFt ?? null } : null,
  }));
}

// Recharts' native tooltip relies on per-dot DOM hit-testing (whichever tiny
// <circle> happens to be topmost in the SVG under the cursor). Where dots
// overlap densely — e.g. near the center of the friction circle — that pick
// depends on draw order, not proximity, so the reported G values jump around
// as the mouse moves. Finding the true nearest point in pixel space instead
// keeps the readout stable.
const HOVER_MAX_PX = 25;

function findNearestPoint(px, py, series, xScale, yScale) {
  let best = null;
  let bestD2 = HOVER_MAX_PX * HOVER_MAX_PX;
  for (const s of series) {
    for (const pt of s.points) {
      const cx = xScale(pt.x);
      const cy = yScale(pt.y);
      if (!isFinite(cx) || !isFinite(cy)) continue;
      const dx = cx - px;
      const dy = cy - py;
      const d2 = dx * dx + dy * dy;
      if (d2 < bestD2) { bestD2 = d2; best = pt; }
    }
  }
  return best;
}

export default function FrictionCircle({
  lapTraces     = {},
  selectedLaps  = [],
  compareDrivers = [],
  customColors1 = null,
  trackName      = '',
  sessionDate    = '',
  sessionVehicle = '',
}) {
  const { chartColors } = useTheme();
  const [gripLimit,  setGripLimit]  = useState(1.5);
  const [dotSize,    setDotSize]    = useState(2);
  const [zoomDomain, setZoomDomain] = useState(null); // [distFtMin, distFtMax] | null
  const [hoverPoint, setHoverPoint] = useState(null); // nearest point under the local cursor, for the tooltip
  const chartWrapRef = useRef(null);
  const canvasRef   = useRef(null);
  const scalesRef   = useRef({ xScale: null, yScale: null });
  const hoverRef    = useRef(null);
  const rafRef      = useRef(null);
  const moveRef     = useRef(null);
  const moveRafRef  = useRef(null);
  // Keep refs in sync with latest render values so the rAF callback always
  // reads current data without capturing stale closure values.
  const seriesRef   = useRef([]);
  const dotSizeRef  = useRef(dotSize);
  dotSizeRef.current = dotSize;

  // Listen for zoom events from any trace chart
  useEffect(() => {
    const handler = (e) => {
      setZoomDomain(e.detail ? [e.detail.distFtMin, e.detail.distFtMax] : null);
    };
    window.addEventListener('trackapp:chart-zoom', handler);
    return () => window.removeEventListener('trackapp:chart-zoom', handler);
  }, []);

  // Sync cursor position from time-series charts via rAF — no React re-render
  useEffect(() => {
    const handler = (e) => {
      hoverRef.current = e.detail?.distFt ?? null;
      if (!rafRef.current) {
        rafRef.current = requestAnimationFrame(() => {
          rafRef.current = null;
          const { xScale, yScale } = scalesRef.current;
          drawCanvas(canvasRef.current, xScale, yScale, seriesRef.current, dotSizeRef.current, hoverRef.current);
        });
      }
    };
    window.addEventListener('trackapp:chart-hover', handler);
    // Late mount (mobile tab switch): show the last cursor position.
    const last = lastCursor();
    if (last) handler({ detail: last });
    return () => {
      window.removeEventListener('trackapp:chart-hover', handler);
      if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    };
  }, []);

  const series1 = useMemo(
    () => buildSeries(lapTraces,  selectedLaps,  LAP_COLORS,     0,  '',  zoomDomain, customColors1),
    [lapTraces, selectedLaps, zoomDomain, customColors1]
  );
  const series2 = useMemo(
    () => compareDrivers.flatMap(d =>
      buildSeries(d.lapTraces, d.selectedLaps, d.palette, 0, `c${d.slot}`, zoomDomain, d.customColors)),
    [compareDrivers, zoomDomain]
  );
  const allSeries = [...series1, ...series2];
  seriesRef.current = allSeries;

  // Stable callback passed to CanvasDotsLayer — reads current values from refs
  const handleCanvasDraw = useCallback(() => {
    const { xScale, yScale } = scalesRef.current;
    drawCanvas(canvasRef.current, xScale, yScale, seriesRef.current, dotSizeRef.current, hoverRef.current);
  }, []);

  const domain = useMemo(() => {
    let max = gripLimit;
    for (const s of allSeries) {
      for (const p of s.points) {
        if (Math.abs(p.x) > max) max = Math.abs(p.x);
        if (Math.abs(p.y) > max) max = Math.abs(p.y);
      }
    }
    const padded = Math.ceil(max * 10) / 10 + 0.1;
    return [-padded, padded];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [series1, series2, gripLimit]);

  // A transparent overlay div (see .hoverLayer below) sits above the chart and
  // canvas and owns all mouse tracking, so the nearest point is always found
  // by true pixel distance rather than by whichever tiny SVG dot happens to
  // be on top in the DOM (see HOVER_MAX_PX comment above findNearestPoint).
  const handleInternalMouseMove = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    moveRef.current = { px: e.clientX - rect.left, py: e.clientY - rect.top };
    if (moveRafRef.current) return;
    moveRafRef.current = requestAnimationFrame(() => {
      moveRafRef.current = null;
      const { xScale, yScale } = scalesRef.current;
      if (!xScale || !yScale || !moveRef.current) return;
      const { px, py } = moveRef.current;
      const nearest = findNearestPoint(px, py, seriesRef.current, xScale, yScale);
      setHoverPoint(nearest ? { x: nearest.x, y: nearest.y, px, py } : null);
      hoverRef.current = nearest?.dist_ft ?? null;
      drawCanvas(canvasRef.current, xScale, yScale, seriesRef.current, dotSizeRef.current, hoverRef.current);
      dispatchHover(nearest?.lat ?? null, nearest?.lon ?? null, nearest?.dist_ft ?? null);
    });
  }, []);

  // A click seeks the video (and every panel listening on chart-click) to the
  // dot under the cursor. The dot may belong to a comparison driver, whose
  // csv_time is on that driver's clock, so the video time comes from the
  // primary session's nearest sample by distance — the same rule SpeedTrace
  // applies — while the dot itself supplies the map position.
  const handleInternalClick = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const { xScale, yScale } = scalesRef.current;
    if (!xScale || !yScale) return;
    const nearest = findNearestPoint(e.clientX - rect.left, e.clientY - rect.top, seriesRef.current, xScale, yScale);
    if (!nearest || nearest.lat == null || nearest.lon == null) return;
    const distFt = nearest.dist_ft ?? null;
    let videoTime = nearest.csv_time ?? null;
    const primary = seriesRef.current.find(s => !s.key.startsWith('c'));
    if (primary && distFt != null) {
      let best = null, bestD = Infinity;
      for (const pt of primary.points) {
        const d = Math.abs((pt.dist_ft ?? 0) - distFt);
        if (d < bestD) { bestD = d; best = pt; }
      }
      if (best?.csv_time != null) videoTime = best.csv_time;
    }
    window.dispatchEvent(new CustomEvent('trackapp:chart-click', {
      detail: { lat: nearest.lat, lon: nearest.lon, distFt, videoTime },
    }));
  }, []);

  const handleMouseLeave = useCallback(() => {
    moveRef.current = null;
    setHoverPoint(null);
    hoverRef.current = null;
    const { xScale, yScale } = scalesRef.current;
    drawCanvas(canvasRef.current, xScale, yScale, seriesRef.current, dotSizeRef.current, null);
    dispatchHover(null, null);
  }, []);

  useEffect(() => () => {
    if (moveRafRef.current) cancelAnimationFrame(moveRafRef.current);
  }, []);

  const resetZoom = () => {
    window.dispatchEvent(new CustomEvent('trackapp:chart-zoom', { detail: null }));
  };

  // Same shape as ScatterPlot's export: the SVG only carries axes, grid and the
  // grip circle — the dots are on the canvas, so it goes in as an overlay.
  // Redraw without the hover ring first; a cursor parked on another panel can
  // leave one behind.
  const handleExportPng = () => {
    const svg = chartWrapRef.current?.querySelector('svg.recharts-surface');
    if (!svg || !canvasRef.current) return;
    const { xScale, yScale } = scalesRef.current;
    drawCanvas(canvasRef.current, xScale, yScale, seriesRef.current, dotSizeRef.current, null);
    const slug = value => String(value || '').replace(/[^\w-]+/g, '_').replace(/^_+|_+$/g, '');
    const host = window.location.hostname;
    exportChartPng({
      svg,
      overlays: [canvasRef.current],
      themeFrom: chartWrapRef.current,
      title: [trackName, sessionVehicle, sessionDate].filter(Boolean).join(' · ') || 'Friction Circle',
      subtitle: `Friction Circle · grip limit ${gripLimit} G${zoomDomain ? ' · zoomed' : ''}`,
      legend: allSeries.map(s => ({ color: s.color, label: s.label })),
      plotTop: 10,
      plotBottom: 20,
      watermark: /^(localhost|127\.|\d+\.\d+\.)/.test(host) ? 'TrackDay Analysis' : host,
      filename: `${[slug(trackName) || 'track', slug(sessionDate), 'friction_circle'].filter(Boolean).join('_')}.png`,
    })
      .catch(error => console.error('Chart export failed:', error))
      .finally(() => drawCanvas(canvasRef.current, xScale, yScale, seriesRef.current, dotSizeRef.current, hoverRef.current));
  };

  return (
    <div className={styles.panel}>
      <PanelHeader className={styles.header} title={<span className={styles.title}>Friction Circle</span>}>
        {zoomDomain && (
          <button className={styles.zoomBadge} onClick={resetZoom} title="Clear zoom">
            ↔ zoomed ×
          </button>
        )}
        <label className={styles.gripLabel}>
          Grip limit
          <input
            type="number"
            className={styles.gripInput}
            value={gripLimit}
            min={0.5}
            max={4}
            step={0.1}
            onChange={e => setGripLimit(parseFloat(e.target.value) || 1.5)}
          />
          G
        </label>
        <label className={styles.dotSizeLabel}>
          Dot
          <input
            type="number"
            className={styles.dotSizeInput}
            value={dotSize}
            min={1}
            max={8}
            step={0.5}
            onChange={e => setDotSize(parseFloat(e.target.value) || 2)}
          />
          px
        </label>
        {allSeries.length > 0 && (
          <div className={styles.legend}>
            {allSeries.map(s => (
              <span key={s.key} className={styles.legendItem}>
                <span className={styles.legendDot} style={{ background: s.color }} />
                {s.label}
              </span>
            ))}
          </div>
        )}
        <button
          type="button"
          className={styles.exportBtn}
          onClick={handleExportPng}
          disabled={allSeries.length === 0}
          title="Download this friction circle as a PNG image with a lap legend"
        >PNG ⤓</button>
        <HelpButton panel="frictioncircle" page="sessions" />
      </PanelHeader>

      <div className={styles.chartWrap} ref={chartWrapRef}>
        {allSeries.length === 0 ? (
          <div className={styles.empty}>
            {zoomDomain
              ? 'No G-force data in the zoomed window'
              : 'Select laps with G-force data to display'}
          </div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart
                accessibilityLayer={false}
                margin={{ top: 10, right: 20, bottom: 20, left: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                <XAxis
                  type="number"
                  dataKey="x"
                  domain={domain}
                  tickCount={9}
                  tickFormatter={fmt2}
                  tick={{ fill: chartColors.tick, fontSize: 10 }}
                  label={{ value: 'Lateral G', position: 'insideBottom', offset: -10, fill: chartColors.tick, fontSize: 11 }}
                />
                <YAxis
                  type="number"
                  dataKey="y"
                  domain={domain}
                  tickCount={9}
                  tickFormatter={fmt2}
                  tick={{ fill: chartColors.tick, fontSize: 10 }}
                  label={{ value: 'Longitudinal G', angle: -90, position: 'insideLeft', offset: 10, fill: chartColors.tick, fontSize: 11 }}
                />
                <ReferenceLine x={0} stroke="#2a2a2a" strokeWidth={1} />
                <ReferenceLine y={0} stroke="#2a2a2a" strokeWidth={1} />
                <Customized component={<GripCircle gripLimit={gripLimit} />} />
                <Customized
                  component={CanvasDotsLayer}
                  canvasRef={canvasRef}
                  scalesRef={scalesRef}
                  onDraw={handleCanvasDraw}
                />
                {allSeries.map(s => (
                  <Scatter
                    key={s.key}
                    name={s.label}
                    data={s.points}
                    fill="transparent"
                    r={dotSize}
                    shape={HitDot}
                  />
                ))}
              </ScatterChart>
            </ResponsiveContainer>
            <canvas ref={canvasRef} className={styles.dotsCanvas} />
            <div
              className={styles.hoverLayer}
              onMouseMove={handleInternalMouseMove}
              onMouseLeave={handleMouseLeave}
              onClick={handleInternalClick}
            />
            {hoverPoint && (
              <div
                className={styles.tooltip}
                style={{ left: hoverPoint.px + 12, top: hoverPoint.py + 12 }}
              >
                <span>Lat G: {fmt2(hoverPoint.x)}</span>
                <span>Lon G: {fmt2(hoverPoint.y)}</span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
